// This file contains helper functions for highlighting functionality
// The main highlighting logic is now in HighlightCommand.js

import { $getSelection, $isRangeSelection } from 'lexical';
import { $isStyledTextNode } from '../StyledTextNode';
import { $isHighlightNode } from './HighlightNode';

/**
 * Check if the current selection contains any highlighted text
 */
export function hasHighlightInSelection() {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return false;
  }

  const nodes = selection.getNodes();
  return nodes.some((node) => {
    if ($isHighlightNode(node)) {
      return true;
    }
    if ($isStyledTextNode(node)) {
      return Boolean(node.getRichStyle()?.backgroundColor);
    }
    return false;
  });
}

/**
 * Get all highlight colors in the current selection
 */
export function getHighlightColorsInSelection() {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return [];
  }

  const nodes = selection.getNodes();
  const colors = [];

  nodes.forEach((node) => {
    if ($isHighlightNode(node)) {
      colors.push(node.getHighlightColor());
      return;
    }
    if ($isStyledTextNode(node)) {
      const bg = node.getRichStyle()?.backgroundColor;
      if (bg) {
        colors.push(bg);
      }
    }
  });

  return colors;
}

/**
 * Check if the current selection has a specific highlight color
 */
export function hasHighlightColorInSelection(color) {
  const colors = getHighlightColorsInSelection();
  return colors.includes(color);
}

/**
 * Get the most common highlight color in the current selection
 */
export function getMostCommonHighlightColorInSelection() {
  const colors = getHighlightColorsInSelection();

  if (colors.length === 0) {
    return null;
  }

  // Count occurrences of each color
  const counts = {};
  colors.forEach((color) => {
    counts[color] = (counts[color] || 0) + 1;
  });

  // Find the most common one
  const result = Object.keys(counts).reduce((a, b) => (counts[a] > counts[b] ? a : b));
  return result;
}
